// I ~29 mila oggetti in orbita come punti luminosi. Le posizioni arrivano dal worker una volta ogni tanto
// (posizione + velocità); tra un aggiornamento e l'altro la scheda video le fa avanzare in linea retta.
// Coordinate: TEME/ECI in km → scena (raggio terrestre = 1): x = x, y = z, z = -y.

import * as THREE from 'three';
import type { Catalogo } from './messaggi';

export const KM_TERRA = 6371;

/** Colori dei tipi nel 3D: stesse tinte dei token (--dato-*), più luminose per brillare sul nero. */
export const COLORI_TIPO = ['#6da7ec', '#9a9a95', '#f0b23a', '#e87ba4', '#9a9a95'];

export class Oggetti {
  readonly punti: THREE.Points;
  private readonly geometria = new THREE.BufferGeometry();
  private readonly materiale: THREE.ShaderMaterial;
  catalogo: Catalogo | null = null;
  pos: Float32Array | null = null;
  vel: Float32Array | null = null;
  tBase = 0;

  constructor() {
    this.materiale = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      uniforms: {
        uDt: { value: 0 },
        uDpr: { value: 1 },
        uVisibili: { value: new THREE.Vector4(1, 1, 1, 1) },
        uColori: { value: COLORI_TIPO.map((c) => new THREE.Color(c)) },
        uOpacita: { value: 1 },
        uScelto: { value: -1 },
        uEvidenza: { value: -1 },
        uAnnoMassimo: { value: 1e5 },
      },
      vertexShader: /* glsl */ `
        attribute vec3 aPos;
        attribute vec3 aVel;
        attribute float aTipo;
        attribute float aIndice;
        attribute float aComparsa;
        uniform float uAnnoMassimo;
        uniform float uDt;
        uniform float uDpr;
        uniform vec4 uVisibili;
        uniform vec3 uColori[5];
        uniform float uScelto;
        uniform float uEvidenza;
        varying vec3 vColore;
        varying float vForza;
        void main() {
          int t = int(aTipo + 0.5);
          float vis = t == 0 ? uVisibili.x : t == 1 ? uVisibili.y : t == 2 ? uVisibili.z : t == 3 ? uVisibili.w : uVisibili.y;
          bool scelto = abs(aIndice - uScelto) < 0.5;
          bool evid = abs(aIndice - uEvidenza) < 0.5;
          if ((vis < 0.5 || aComparsa > uAnnoMassimo) && !scelto) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); gl_PointSize = 0.0; return; }
          vec3 p = (aPos + aVel * uDt) / ${KM_TERRA.toFixed(1)};
          vec4 mv = modelViewMatrix * vec4(p.x, p.z, -p.y, 1.0);
          gl_Position = projectionMatrix * mv;
          float base = t == 3 ? 1.5 : t == 2 ? 2.3 : 2.0;
          float dimensione = base * uDpr * clamp(4.0 / -mv.z, 0.55, 2.4);
          vColore = uColori[t];
          vForza = t == 3 ? 0.7 : 0.95;
          if (scelto || evid) { dimensione = 14.0 * uDpr; vColore = vec3(1.0); vForza = 1.6; }
          gl_PointSize = dimensione;
        }`,
      fragmentShader: /* glsl */ `
        uniform float uOpacita;
        varying vec3 vColore;
        varying float vForza;
        void main() {
          vec2 c = gl_PointCoord - 0.5;
          float r = length(c);
          float nucleo = smoothstep(0.5, 0.0, r);
          float a = pow(nucleo, 1.6) * vForza * uOpacita;
          if (a < 0.01) discard;
          gl_FragColor = vec4(vColore * a * 1.6, a);
        }`,
    });
    this.punti = new THREE.Points(this.geometria, this.materiale);
    this.punti.frustumCulled = false;
    this.punti.renderOrder = 3;
  }

  impostaCatalogo(c: Catalogo): void {
    this.catalogo = c;
    const n = c.tipi.length;
    this.geometria.setAttribute('aPos', new THREE.BufferAttribute(new Float32Array(n * 3), 3));
    this.geometria.setAttribute('aVel', new THREE.BufferAttribute(new Float32Array(n * 3), 3));
    this.geometria.setAttribute('aTipo', new THREE.BufferAttribute(Float32Array.from(c.tipi), 1));
    this.geometria.setAttribute('aIndice', new THREE.BufferAttribute(Float32Array.from({ length: n }, (_, i) => i), 1));
    this.geometria.setAttribute('aComparsa', new THREE.BufferAttribute(c.comparse, 1));
    this.geometria.setDrawRange(0, 0);
  }

  impostaPosizioni(t: number, pos: Float32Array, vel: Float32Array): void {
    this.tBase = t;
    this.pos = pos;
    this.vel = vel;
    const aPos = this.geometria.getAttribute('aPos') as THREE.BufferAttribute;
    const aVel = this.geometria.getAttribute('aVel') as THREE.BufferAttribute;
    // Oggetti non calcolabili (decaduti, elementi errati): nascosti lontano.
    for (let i = 0; i < pos.length; i += 3) {
      if (!Number.isFinite(pos[i])) { pos[i] = 1e9; pos[i + 1] = 0; pos[i + 2] = 0; vel[i] = vel[i + 1] = vel[i + 2] = 0; }
    }
    (aPos.array as Float32Array).set(pos);
    (aVel.array as Float32Array).set(vel);
    aPos.needsUpdate = true;
    aVel.needsUpdate = true;
    this.geometria.setDrawRange(0, pos.length / 3);
  }

  /** Posizione nella scena dell'oggetto i al tempo t (ms). */
  posizioneScena(i: number, t: number, out: THREE.Vector3): THREE.Vector3 | null {
    if (!this.pos || !this.vel) return null;
    const dt = (t - this.tBase) / 1000;
    const x = this.pos[i * 3] + this.vel[i * 3] * dt;
    if (x > 1e8) return null;
    const y = this.pos[i * 3 + 1] + this.vel[i * 3 + 1] * dt;
    const z = this.pos[i * 3 + 2] + this.vel[i * 3 + 2] * dt;
    return out.set(x / KM_TERRA, z / KM_TERRA, -y / KM_TERRA);
  }

  aggiorna(t: number, dpr: number, opacita: number): void {
    const u = this.materiale.uniforms;
    u.uDt.value = (t - this.tBase) / 1000;
    u.uDpr.value = dpr;
    u.uOpacita.value = opacita;
  }

  set visibili(v: [boolean, boolean, boolean, boolean]) {
    (this.materiale.uniforms.uVisibili.value as THREE.Vector4).set(+v[0], +v[1], +v[2], +v[3]);
  }

  tipoVisibile(tipo: number): boolean {
    const v = this.materiale.uniforms.uVisibili.value as THREE.Vector4;
    return (tipo === 0 ? v.x : tipo === 2 ? v.z : tipo === 3 ? v.w : v.y) > 0.5;
  }

  /** Mostra solo gli oggetti comparsi entro quest'anno (con frazione); Infinity = tutti. */
  set annoMassimo(anno: number) { this.materiale.uniforms.uAnnoMassimo.value = Number.isFinite(anno) ? anno : 1e5; }

  set scelto(i: number) { this.materiale.uniforms.uScelto.value = i; }
  set evidenza(i: number) { this.materiale.uniforms.uEvidenza.value = i; }
}
